// Client-side react-table filter for the column-menu `{ operator, values }`
// shape, covering the text operators plus the numeric/date comparison operators
// (filterOperators.js).
//
// The text operators are NOT reimplemented here — they delegate to
// textColumnFilterFn unchanged. That function is the filter for every one of the
// ~30 menu-enabled tables, so any drift in it would change filtering everywhere
// at once; adding beside it keeps existing behaviour byte-identical.
//
// DATES ARE FILTERED BY CALENDAR DAY, IN LOCAL TIME (AC-15894/15895/15896).
// The picker yields a day ("2026-08-13") while rows usually hold instants
// ("2026-08-13T14:30:00Z"), and cells are *displayed* with date-fns in local
// time. Comparing the day as an instant made every operator wrong at the
// boundary: Equals matched nothing, On or Before behaved as Before, and Between
// dropped its last day.
//
// A date-only value means "a day in the viewer's timezone" — and that holds for
// ROW values as much as for bounds. Date.parse would read a bare "2026-08-13" as
// UTC midnight, which is the *previous* day anywhere west of Greenwich, so a row
// reading 2026-08-13 would fall outside a filter for 2026-08-13. Both sides go
// through the same local construction instead.
//
// Numeric comparison coerces per row: rows whose value can't be coerced are
// excluded from a comparison (you asked for "> 5"; a row holding "N/A" is not
// greater than 5), while blanks are excluded from everything except
// empty/nempty.

import textColumnFilterFn from "./textColumnFilterFn";
import { isBlank } from "./columnTypeOf";

const NUMERIC_RE = /^-?\d+(\.\d+)?$/;
// Date with a time component — a genuine instant, parsed as-is.
const DATE_TIME_RE = /^\d{4}-\d{2}-\d{2}[T ]/;
// Date with no time component — a day, anchored in local time.
const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$|^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;

const COMPARISON_OPERATORS = [
  "gt",
  "gte",
  "lt",
  "lte",
  "before",
  "onOrBefore",
  "after",
  "onOrAfter",
  "between",
];

const EQUALITY_OPERATORS = ["eq", "neq"];

function asNumber(v) {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v !== "string") return null;
  const t = v.trim();
  return NUMERIC_RE.test(t) ? Number(t) : null;
}

// [start, end] of the local calendar day a date-only value names, or null if it
// isn't a bare date. Built via the Date(y, m, d) form so the day is anchored in
// the viewer's timezone, matching how cells are rendered.
function localDayRange(v) {
  if (typeof v !== "string") return null;
  const m = DATE_ONLY_RE.exec(v.trim());
  if (!m) return null;
  const [y, mo, d] = m[1] ? [+m[1], +m[2], +m[3]] : [+m[6], +m[4], +m[5]];
  const start = new Date(y, mo - 1, d, 0, 0, 0, 0);
  // Out-of-range components roll over silently (Feb 30 becomes Mar 2), so
  // confirm the date we got back is the one that was asked for.
  if (
    start.getFullYear() !== y ||
    start.getMonth() !== mo - 1 ||
    start.getDate() !== d
  ) {
    return null;
  }
  return {
    start: start.getTime(),
    end: new Date(y, mo - 1, d, 23, 59, 59, 999).getTime(),
  };
}

// Instant for a value. Date-only values resolve to local midnight — inside their
// own local day — so they compare consistently against locally-widened bounds.
function asDate(v) {
  if (v instanceof Date) {
    const t = v.getTime();
    return Number.isNaN(t) ? null : t;
  }
  if (typeof v !== "string") return null;
  const t = v.trim();
  const day = localDayRange(t);
  if (day) return day.start;
  if (!DATE_TIME_RE.test(t)) return null;
  const parsed = Date.parse(t);
  return Number.isNaN(parsed) ? null : parsed;
}

// Comparable number for a cell/filter value: numeric if it looks numeric,
// otherwise a timestamp if it looks like a date. null when neither.
function comparable(v) {
  const n = asNumber(v);
  if (n !== null) return n;
  return asDate(v);
}

// Does this row's value fall on the day the bound names? null when the row isn't
// a date at all, so the caller can decide what that means per operator.
function matchesDay(raw, day) {
  if (isBlank(raw)) return null;
  if (asNumber(raw) !== null) return null;
  const v = asDate(raw);
  if (v === null) return null;
  return v >= day.start && v <= day.end;
}

export default function columnFilterFn(rows, columnIds, filterValue) {
  if (!filterValue || !filterValue.operator) return rows;

  const { operator, values = [] } = filterValue;
  const id = columnIds[0];

  // Equality against a date: compare calendar days rather than strings, decided
  // PER ROW. A column-wide check would have to sample `rows`, which react-table
  // has already narrowed by any other active filter — the same two filters would
  // then give different results depending on the order they were applied.
  if (EQUALITY_OPERATORS.includes(operator)) {
    const day = localDayRange(values[0]);
    if (!day) {
      // Bound isn't a date, so this is ordinary text/numeric equality.
      return textColumnFilterFn(rows, columnIds, filterValue);
    }
    return rows.filter((row) => {
      const sameDay = matchesDay(row.values[id], day);
      // A blank or non-date row can't equal a date — but it is genuinely "not
      // equal" to one, so Does Not Equal must keep it.
      if (sameDay === null) return operator === "neq";
      return operator === "eq" ? sameDay : !sameDay;
    });
  }

  if (!COMPARISON_OPERATORS.includes(operator)) {
    // Text operators (and anything unrecognised) keep their existing behaviour.
    return textColumnFilterFn(rows, columnIds, filterValue);
  }

  const a = comparable(values[0]);
  const b = comparable(values[1]);

  // An unusable bound means the filter can't be evaluated — leave rows alone
  // rather than silently emptying the table.
  if (a === null) return rows;
  if (operator === "between" && b === null) return rows;

  // Widen date-only bounds to their whole local day. Numeric bounds, and bounds
  // that already carry a time, keep their exact value.
  const dayA = localDayRange(values[0]);
  const dayB = localDayRange(values[1]);
  const startA = dayA ? dayA.start : a;
  const endA = dayA ? dayA.end : a;

  // Tolerate a reversed range instead of returning nothing.
  let lo = startA;
  let hi = dayB ? dayB.end : b;
  if (operator === "between" && lo > hi) {
    lo = dayB ? dayB.start : b;
    hi = dayA ? dayA.end : a;
  }

  return rows.filter((row) => {
    const raw = row.values[id];
    if (isBlank(raw)) return false;
    const v = comparable(raw);
    if (v === null) return false;

    switch (operator) {
      // Strictly outside the named day.
      case "gt":
      case "after":
        return v > endA;
      case "lt":
      case "before":
        return v < startA;
      // Inclusive of the named day.
      case "gte":
      case "onOrAfter":
        return v >= startA;
      case "lte":
      case "onOrBefore":
        return v <= endA;
      case "between":
        return v >= lo && v <= hi;
      default:
        return true;
    }
  });
}
